import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

// Web auth mirrors src/lib/auth.ts (RN) at the data-model level but uses
// Supabase's hosted OAuth redirect flow instead of the native Google
// Sign-In SDK.

export interface Profile {
  id: string;
  display_name: string;
  phone_number: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface HomeMember {
  user_id: string;
  display_name: string | null;
  phone_number: string | null;
  avatar_url: string | null;
  joined_at: string;
}

export class SignInCancelledError extends Error {
  constructor() {
    super("Sign-in cancelled");
    this.name = "SignInCancelledError";
  }
}

/**
 * Kick off the Supabase hosted Google OAuth flow. This redirects the
 * browser away; on return, detectSessionInUrl (in supabase.ts) picks the
 * session out of the callback URL and `onAuthStateChange` fires.
 */
export async function signInWithGoogle(): Promise<void> {
  const redirectTo = `${window.location.origin}/auth/callback`;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function isEmailAllowed(email: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("allowed_users")
    .select("email")
    .eq("email", email.toLowerCase())
    .maybeSingle();
  if (error) throw error;
  return data !== null;
}

export async function getSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export function onAuthStateChange(
  callback: (session: Session | null) => void
) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
  return data.subscription;
}

export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Normalize phone to +91XXXXXXXXXX for storage. */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, "");
  if (digits.startsWith("0")) return "+91" + digits.slice(1);
  if (digits.length === 10) return "+91" + digits;
  if (digits.startsWith("91") && digits.length === 12) return "+" + digits;
  return "+" + digits;
}

export async function createProfile(
  userId: string,
  displayName: string,
  phoneNumber: string | null,
  avatarUrl: string | null = null
): Promise<Profile> {
  const { data, error } = await supabase
    .from("profiles")
    .insert({
      id: userId,
      display_name: displayName,
      phone_number: phoneNumber ? normalizePhone(phoneNumber) : null,
      avatar_url: avatarUrl,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateProfile(
  userId: string,
  updates: {
    display_name?: string;
    phone_number?: string | null;
    avatar_url?: string | null;
  }
): Promise<Profile> {
  const normalized = { ...updates, updated_at: new Date().toISOString() };
  if (normalized.phone_number) {
    normalized.phone_number = normalizePhone(normalized.phone_number);
  }
  const { data, error } = await supabase
    .from("profiles")
    .update(normalized)
    .eq("id", userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchHomeMembers(
  homeId: string
): Promise<HomeMember[]> {
  const { data, error } = await supabase.rpc("get_home_members", {
    home_id: homeId,
  });
  if (error) throw error;
  return (data || []) as HomeMember[];
}
